import { useEffect, useMemo, useState, type FormEvent } from "react";
import { DepartmentFilter, useDepartments } from "../../hooks/useDepartments";
import { addDoc, collection, doc, getDoc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { editStamp } from "../../lib/editStamp";
import { lotKey, paths, ROLE_RANK, type Customer, type Job, type JobStatus, type NotificationSettings } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { textMatches } from "../../lib/filter";
import { downloadCsv, csvDate } from "../../lib/csv";
import { useAuth } from "../../hooks/useAuth";
import { useIndustry } from "../../hooks/useIndustry";
import { CustomerPicker, customerAddress } from "../../components/CustomerPicker";
import { JobTypeTitle, JobTypesSetting, finalizeJobTitle } from "../../components/JobTypeTitle";
import { textareaClass } from "../../components/JobEditCard";
import { useJobSettings } from "../../hooks/useJobSettings";
import { useAccess } from "../../hooks/useAccess";
import { ProjectItemSelect, jobItemCopy, useProjectItems } from "../../components/ProjectItems";
import { resolveLot, useProjectLots } from "../../lib/lots";
import { AddressField, locatedOf, type Located } from "../../components/AddressField";
import { JobDetail } from "./JobDetail";

type Row = Job & { id: string };

const STATUS_LABEL: Record<JobStatus, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  done: "Done",
  canceled: "Canceled",
};
const STATUS_STYLE: Record<JobStatus, string> = {
  scheduled: "bg-steel/15 text-ink",
  in_progress: "bg-brandorange/15 text-brandorange-dark",
  done: "bg-success/15 text-success",
  canceled: "bg-steel/10 text-steel line-through",
};
// The one allowed next step per status — no nine-pill scroll strips here.
const NEXT: Partial<Record<JobStatus, { to: JobStatus; label: string }>> = {
  scheduled: { to: "in_progress", label: "Start" },
  in_progress: { to: "done", label: "Finish" },
};

type SortKey = "scheduled" | "newest" | "updated" | "finished";
type ShowKey = "all" | "unassigned" | "nodate" | "pastdue" | "today" | "week" | "mine";
const SORT_LABEL: Record<SortKey, string> = {
  scheduled: "By scheduled date",
  newest: "Newest first",
  updated: "Recently changed",
  finished: "Recently finished",
};
const SHOW_LABEL: Record<ShowKey, string> = {
  all: "All",
  unassigned: "Unassigned",
  nodate: "No date yet",
  pastdue: "Past due",
  today: "Today",
  week: "Next 7 days",
  mine: "Assigned to me",
};
const PAGE = 200;
const EMPTY_FORM = { jobType: "", payMode: "" as "" | "hourly" | "piece", lot: "", projectItemId: "", title: "", customerId: "", customerName: "", date: "", start: "08:00", end: "10:00", address: "", note: "", departmentId: "", projectId: "" };

export function JobsPage() {
  const { tenantId, user, role } = useAuth();
  const { vocab } = useIndustry();
  const isAdmin = !!role && ROLE_RANK[role] >= ROLE_RANK.admin;
  // Field Tech: only jobs assigned to them; they move jobs along but don't
  // create, assign, cancel or export (Firestore rules enforce the same).
  const isTech = role === "tech";
  const [openRows, setOpenRows] = useState<Row[]>([]);
  const [doneRows, setDoneRows] = useState<Row[]>([]);
  const [fetchedNames, setFetchedNames] = useState<Record<string, string>>({});
  const [members, setMembers] = useState<Array<{ uid: string; name: string }>>([]);
  const [tab, setTab] = useState<"open" | "done">("open");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("scheduled");
  const [show, setShow] = useState<ShowKey>("all");
  const [limit, setLimit] = useState(PAGE);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loc, setLoc] = useState<Located>(null);
  const { types: jobTypes, pay: jobPay } = useJobSettings(tenantId);
  const projectItems = useProjectItems(tenantId, form.projectId || null);
  const projectLots = useProjectLots(tenantId, form.projectId || null, !isTech);
  // A known lot fills in what the new job doesn't have yet: its address and elevation.
  useEffect(() => {
    const hit = form.lot.trim() ? projectLots.find((l) => l.key === lotKey(form.lot)) : undefined;
    if (!hit) return;
    if (!form.address.trim() && locatedOf(hit)) setLoc(locatedOf(hit));
    setForm((f) => ({
      ...f,
      address: f.address || hit.address,
      projectItemId: f.projectItemId || (hit.projectItemId && projectItems.some((i) => i.id === hit.projectItemId) ? hit.projectItemId : ""),
    }));
  }, [form.lot, projectLots, projectItems]);
  const [showSettings, setShowSettings] = useState(() => typeof window !== "undefined" && window.location.hash === "#job-settings");
  const can = useAccess();
  // The department this person works in (remembered; techs see their own jobs).
  const dept = useDepartments();
  // A pinned "Job settings" favorite lands here with #job-settings.
  useEffect(() => {
    const open = (e: Event) => {
      if ((e as CustomEvent<string>).detail === "job-settings") setShowSettings(true);
    };
    window.addEventListener("clevis:hash", open);
    return () => window.removeEventListener("clevis:hash", open);
  }, []);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; customerId: string | null }>>([]);

  useEffect(() => {
    if (!tenantId || isTech) return;
    return onSnapshot(
      query(collection(db, paths.tenantProjects(tenantId)), where("status", "==", "active")),
      (snap) => setProjects(snap.docs.map((d) => ({ id: d.id, name: (d.get("name") as string) ?? "", customerId: (d.get("customerId") as string | null) ?? null })).sort((x, y) => x.name.localeCompare(y.name))),
    );
  }, [tenantId, isTech]);
  // "New job for this lot" on a project lands here with ?project=…&lot=….
  useEffect(() => {
    if (!tenantId || isTech) return;
    const sp = new URLSearchParams(window.location.search);
    const projectId = sp.get("project");
    if (!projectId) return;
    const lot = sp.get("lot") ?? "";
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.hash);
    void getDoc(doc(db, paths.tenantProject(tenantId, projectId))).then(async (p) => {
      if (!p.exists()) return;
      const customerId = (p.get("customerId") as string | null) ?? "";
      const c = customerId ? await getDoc(doc(db, paths.tenantCustomer(tenantId, customerId))) : null;
      setForm((f) => ({ ...f, projectId, lot, customerId, customerName: (c?.get("displayName") as string | undefined) ?? "" }));
      setShowCreate(true);
    });
  }, [tenantId, isTech]);
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!tenantId || isTech) return;
    return onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
      // Jobs route to departments, never to cross-department groups.
      setDepartments(snap.docs.filter((d) => d.get("kind") !== "group").map((d) => ({ id: d.id, name: (d.get("name") as string) ?? "" }))),
    );
  }, [tenantId, isTech]);

  // Open jobs always; finished ones only once the Done tab is opened (a big
  // company has thousands). A tech's own jobs are few, so one query covers both.
  useEffect(() => {
    if (!tenantId || !user) return;
    const toRows = (docs: Array<{ id: string; data: () => unknown }>) => docs.map((d) => ({ id: d.id, ...(d.data() as Job) }));
    const subs = [
      isTech
        ? onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("assignedUids", "array-contains", user.uid)), (snap) => {
            const all = toRows(snap.docs);
            setOpenRows(all.filter((r) => r.status === "scheduled" || r.status === "in_progress"));
            setDoneRows(all.filter((r) => r.status === "done" || r.status === "canceled"));
          })
        : onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("status", "in", ["scheduled", "in_progress"])), (snap) =>
            setOpenRows(toRows(snap.docs)),
          ),
      onSnapshot(collection(db, paths.tenantMembers(tenantId)), (snap) =>
        setMembers(
          snap.docs
            .filter((d) => d.get("disabled") !== true)
            .map((d) => ({ uid: d.id, name: (d.get("displayName") as string) || (d.get("email") as string) })),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, user, isTech]);

  useEffect(() => {
    if (!tenantId || isTech || tab !== "done") return;
    return onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("status", "in", ["done", "canceled"])), (snap) =>
      setDoneRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
    );
  }, [tenantId, isTech, tab]);

  useEffect(() => {
    setLimit(PAGE);
    if (tab === "done" && sort === "scheduled") setSort("finished");
    if (tab === "open" && sort === "finished") setSort("scheduled");
  }, [tab, sort, show, search]);

  const rows = tab === "open" ? openRows : doneRows;

  // Jobs carry customerName (filled by the server); fetch the few that don't yet.
  useEffect(() => {
    if (!tenantId) return;
    const missing = [
      ...new Set(rows.filter((r) => r.customerId && r.customerName === undefined).map((r) => r.customerId as string)),
    ]
      .filter((id) => !(id in fetchedNames))
      .slice(0, 100);
    if (missing.length === 0) return;
    void Promise.all(
      missing.map(async (id) => {
        const snap = await getDoc(doc(db, paths.tenantCustomer(tenantId, id))).catch(() => null);
        return [id, snap?.exists() ? ((snap.data() as Customer).displayName ?? "") : ""] as const;
      }),
    ).then((found) => setFetchedNames((prev) => ({ ...prev, ...Object.fromEntries(found) })));
  }, [tenantId, rows, fetchedNames]);

  const customerName = (r: Row) => r.customerName ?? (r.customerId ? (fetchedNames[r.customerId] ?? "") : "");
  const memberNames = useMemo(() => new Map(members.map((m) => [m.uid, m.name])), [members]);
  const memberName = (uid: string) => memberNames.get(uid) ?? "?";

  const visible = useMemo(() => {
    const today = new Date().setHours(0, 0, 0, 0);
    const tomorrow = today + 86400000;
    const inShow = (r: Row) => {
      switch (show) {
        case "unassigned":
          return r.assignedUids.length === 0;
        case "nodate":
          return !r.startAt;
        case "pastdue":
          return !!r.startAt && r.startAt < today && (r.status === "scheduled" || r.status === "in_progress");
        case "today":
          return !!r.startAt && r.startAt >= today && r.startAt < tomorrow;
        case "week":
          return !!r.startAt && r.startAt >= today && r.startAt < today + 7 * 86400000;
        case "mine":
          return !!user && r.assignedUids.includes(user.uid);
        default:
          return true;
      }
    };
    const out = rows.filter(
      (r) =>
        inShow(r) &&
        (isTech || dept.jobMatches(r)) &&
        textMatches(search, r.title, r.address, r.note, r.poNumber, STATUS_LABEL[r.status], customerName(r), ...r.assignedUids.map(memberName)),
    );
    const rank = (t: number | null) => (!t ? 1 : t >= today ? 0 : 2);
    const by: Record<SortKey, (a: Row, b: Row) => number> = {
      scheduled: (a, b) =>
        rank(a.startAt) - rank(b.startAt) ||
        (rank(a.startAt) === 2 ? (b.startAt ?? 0) - (a.startAt ?? 0) : (a.startAt ?? 0) - (b.startAt ?? 0)),
      newest: (a, b) => b.createdAt - a.createdAt,
      updated: (a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
      finished: (a, b) => (b.doneAt ?? b.updatedAt ?? 0) - (a.doneAt ?? a.updatedAt ?? 0),
    };
    return out.sort(by[sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, sort, show, fetchedNames, memberNames, user, dept.current, dept.enabled]);
  const page = visible.slice(0, limit);

  function exportCsv() {
    downloadCsv(
      `jobs-${tab}`,
      ["Title", "Customer", "Status", "Date", "Tech", "Address", "PO", "ETA", "Notes"],
      visible.map((r) => [
        r.title,
        customerName(r),
        STATUS_LABEL[r.status],
        csvDate(r.startAt),
        r.assignedUids.map(memberName).join("; "),
        r.address,
        r.poNumber ?? "",
        r.plannedEtaAt ? new Date(r.plannedEtaAt).toLocaleTimeString() : "",
        r.note,
      ]),
    );
  }

  // Scheduled-date view of open jobs: grouped by day in the order above
  // (upcoming, undated, then past due), unassigned surfacing first in a day.
  const dayGroups = useMemo(() => {
    const byDay = new Map<string, { label: string; jobs: Row[] }>();
    const today = new Date().setHours(0, 0, 0, 0);
    const todayKey = new Date().toDateString();
    const tomorrowKey = new Date(Date.now() + 86400000).toDateString();
    for (const j of page) {
      const key = j.startAt ? new Date(j.startAt).toDateString() : "unscheduled";
      if (!byDay.has(key)) {
        const label =
          key === "unscheduled"
            ? "No date yet"
            : (key === todayKey ? "Today — " : key === tomorrowKey ? "Tomorrow — " : j.startAt! < today ? "Past due — " : "") +
              new Date(j.startAt!).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
        byDay.set(key, { label, jobs: [] });
      }
      byDay.get(key)!.jobs.push(j);
    }
    const groups = [...byDay.values()];
    for (const g of groups) {
      g.jobs.sort((a, b) => {
        const ua = a.assignedUids.length === 0 ? 0 : 1;
        const ub = b.assignedUids.length === 0 ? 0 : 1;
        if (ua !== ub) return ua - ub;
        return (a.startAt ?? 0) - (b.startAt ?? 0);
      });
    }
    return groups;
  }, [page]);

  async function assignTech(jobId: string, uid: string) {
    if (!tenantId || !uid) return;
    await updateDoc(doc(db, paths.tenantJob(tenantId, jobId)), { assignedUids: [uid], ...editStamp(user) });
  }

  async function createJob(e: FormEvent) {
    e.preventDefault();
    if (!tenantId || !user) return;
    const title = finalizeJobTitle(form.title, form.jobType);
    if (!title) {
      setCreateError("Give the job a title or pick a type.");
      return;
    }
    setBusy(true);
    setCreateError("");
    try {
      const startAt = form.date ? new Date(`${form.date}T${form.start}`).getTime() : null;
      const endAt = form.date ? new Date(`${form.date}T${form.end}`).getTime() : null;
      const item = form.projectId ? projectItems.find((i) => i.id === form.projectItemId) : undefined;
      // The project's lot record for what was typed (found, or made now).
      const lot =
        form.projectId && form.lot.trim()
          ? await resolveLot(tenantId, form.projectId, form.lot, projectLots, { address: form.address, projectItemId: item?.id ?? null, projectItemName: item?.name ?? "" })
          : null;
      const job: Job = {
        title,
        ...(form.jobType ? { jobType: form.jobType } : {}),
        payMode: form.payMode || jobPay[form.jobType]?.mode || "hourly",
        customerId: form.customerId || null,
        ...(form.customerId ? { customerName: form.customerName } : {}),
        locationId: null,
        status: "scheduled",
        startAt,
        endAt,
        assignedUids: [],
        address: form.address,
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
        note: form.note,
        poNumber: null,
        kind: "work",
        parentJobId: null,
        departmentId: form.departmentId || null,
        projectId: form.projectId || null,
        ...(lot ? { lot: lot.name, lotId: lot.id } : {}),
        ...(item ? { projectItem: jobItemCopy(item) } : {}),
        billToCustomerId: null,
        coverage: null,
        sequence: null,
        plannedEtaAt: null,
        serviceMinutes: null,
        geocodeTriedAt: null,
        doneAt: null,
        signedOffAt: null,
        signedOffBy: null,
        createdByUid: user.uid,
        createdAt: Date.now(),
        ...editStamp(user),
      };
      const ref = await addDoc(collection(db, paths.tenantJobs(tenantId)), job);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setLoc(null);
      setOpenJobId(ref.id); // straight to the new job, not back to a list of hundreds
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, status: JobStatus) {
    if (!tenantId) return;
    await updateDoc(doc(db, paths.tenantJob(tenantId, id)), { status, ...editStamp(user) });
  }

  function fmtWhen(job: Row) {
    if (!job.startAt) return "Unscheduled";
    const d = new Date(job.startAt);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "2-digit" }) +
      " " +
      d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }

  function jobRow(job: Row, grouped: boolean) {
    const unassigned = job.assignedUids.length === 0;
    const timeLabel = grouped
      ? job.startAt
        ? new Date(job.startAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
        : "no time"
      : sort === "newest"
        ? `Added ${new Date(job.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${fmtWhen(job)}`
        : fmtWhen(job);
    const cust = customerName(job);
    return (
      <div
        key={job.id}
        className={cn(
          "flex items-center justify-between gap-3 border-b border-steel/10 px-4 py-2 last:border-0",
          grouped && unassigned && job.status === "scheduled" && "bg-brandorange/5",
        )}
      >
        <button className="min-w-0 flex-1 text-left" onClick={() => setOpenJobId(job.id)}>
          <div className="truncate text-sm text-ink hover:text-brandorange">
            {cust ? <span className="font-semibold">{cust}</span> : null}
            {cust ? <span className="text-steel"> · </span> : null}
            <span className={cust ? "" : "font-medium"}>{job.title}</span>
            {job.dryRuns?.length && job.status !== "done" ? (
              <span className="ml-1.5 rounded-full bg-danger/10 px-1.5 py-0.5 text-[10px] font-semibold text-danger" title={String((job.dryRuns[job.dryRuns.length - 1] as { reason?: string }).reason ?? "")}>
                dry run{job.dryRuns.length > 1 ? ` ×${job.dryRuns.length}` : ""}
              </span>
            ) : null}
          </div>
          <div className="truncate text-xs text-steel">
            {timeLabel}
            {job.poNumber ? ` · PO ${job.poNumber}` : ""}
            {job.address ? ` · ${job.address}` : ""}
            {!unassigned ? ` · ${job.assignedUids.map(memberName).join(", ")}` : ""}
            {unassigned && job.fpAssignees?.length
              ? ` · FieldPulse: ${job.fpAssignees.map((a) => a.name || a.email).join(", ")} (joins via invite)`
              : ""}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {grouped && unassigned && job.status === "scheduled" && !isTech ? (
            <Select
              className="w-36 px-2 py-1 text-xs"
              value=""
              onChange={(e) => void assignTech(job.id, e.target.value)}
            >
              <option value="">Assign tech…</option>
              {members.map((m) => (
                <option key={m.uid} value={m.uid}>
                  {m.name}
                </option>
              ))}
            </Select>
          ) : null}
          <span className={cn("rounded-full px-2 py-0.5 text-xs", STATUS_STYLE[job.status])}>
            {STATUS_LABEL[job.status]}
          </span>
          {NEXT[job.status] ? (
            <Button className="px-3 py-1 text-xs" onClick={() => void setStatus(job.id, NEXT[job.status]!.to)}>
              {NEXT[job.status]!.label}
            </Button>
          ) : null}
          {job.status === "scheduled" && !isTech ? (
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void setStatus(job.id, "canceled")}>
              Cancel
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  if (openJobId) {
    return <JobDetail jobId={openJobId} onBack={() => setOpenJobId(null)} />;
  }

  const sortOptions: SortKey[] = tab === "open" ? ["scheduled", "newest", "updated"] : ["finished", "scheduled", "newest", "updated"];
  const showOptions: ShowKey[] = isTech
    ? ["all", "nodate", "pastdue", "today", "week"]
    : tab === "open"
      ? ["all", "unassigned", "nodate", "pastdue", "today", "week", "mine"]
      : ["all", "mine"];
  const grouped = tab === "open" && sort === "scheduled";

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">{isTech ? `My ${vocab.jobs.toLowerCase()}` : vocab.jobs}</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
            {(["open", "done"] as const).map((t) => (
              <button
                key={t}
                className={cn("rounded-md px-3 py-1 capitalize", tab === t ? "bg-ink text-white" : "text-steel")}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          {!isTech ? (
            <Button
              onClick={() => {
                // New work starts in the department you're looking at.
                if (!showCreate) setForm((f) => ({ ...f, departmentId: f.departmentId || dept.defaultForNew }));
                setShowCreate((v) => !v);
              }}
            >
              {showCreate ? "Cancel" : "New job"}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="min-w-48 flex-1"
          placeholder="Search jobs: customer, title, PO, tech, address…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!isTech ? <DepartmentFilter dept={dept} className="w-auto py-1.5 text-xs" /> : null}
        <Select className="w-auto py-1.5 text-xs" aria-label="Show" value={show} onChange={(e) => setShow(e.target.value as ShowKey)}>
          {showOptions.map((k) => (
            <option key={k} value={k}>
              {SHOW_LABEL[k]}
            </option>
          ))}
        </Select>
        <Select className="w-auto py-1.5 text-xs" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          {sortOptions.map((k) => (
            <option key={k} value={k}>
              {SORT_LABEL[k]}
            </option>
          ))}
        </Select>
        {!isTech && can("jobs.export") ? (
          <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={exportCsv}>
            Export CSV
          </button>
        ) : null}
        {can("jobs.settings") ? (
          <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={() => setShowSettings((v) => !v)}>
            {showSettings ? "Close settings" : "Job settings"}
          </button>
        ) : null}
      </div>

      {showSettings && can("jobs.settings") && tenantId ? (
        <Card id="job-settings" className="space-y-4">
          <JobTypesSetting tenantId={tenantId} types={jobTypes} />
          <div className="space-y-2 border-t border-steel/15 pt-3">
            <SignoffSetting tenantId={tenantId} />
            <CompletionEmailSettings tenantId={tenantId} />
          </div>
        </Card>
      ) : null}

      {showCreate && !isTech && tenantId ? (
        <Card>
          <CardTitle>New job</CardTitle>
          <form onSubmit={createJob} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Customer</Label>
              <CustomerPicker
                tenantId={tenantId}
                value={form.customerId}
                valueName={form.customerName}
                onChange={(c) => {
                  if (!form.address.trim() && c) setLoc(locatedOf(c));
                  setForm((f) => ({
                    ...f,
                    customerId: c?.id ?? "",
                    customerName: c?.displayName ?? "",
                    // Fill the job address from the customer unless one was typed.
                    address: f.address || (c ? customerAddress(c) : ""),
                  }));
                }}
              />
            </div>
            <JobTypeTitle
              types={jobTypes}
              jobType={form.jobType}
              title={form.title}
              onChange={(next) => setForm((f) => ({ ...f, ...next }))}
            />
            <div>
              <Label>Pay for this call</Label>
              <Select value={form.payMode || jobPay[form.jobType]?.mode || "hourly"} onChange={(e) => setForm({ ...form, payMode: e.target.value as "hourly" | "piece" })}>
                <option value="hourly">Hourly (time clock)</option>
                <option value="piece">Piece rate</option>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <textarea
                className={textareaClass}
                rows={3}
                placeholder="What's going on? e.g. Customer called: the door won't close all the way."
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
            {projects.length > 0 ? (
              <div>
                <Label>Project</Label>
                <Select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
                  <option value="">— none —</option>
                  {projects.filter((p) => !form.customerId || !p.customerId || p.customerId === form.customerId).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            {form.projectId && projectItems.length > 0 ? (
              <div>
                <Label>Item (bid option)</Label>
                <ProjectItemSelect
                  items={projectItems}
                  value={form.projectItemId}
                  onChange={(it) =>
                    setForm((f) => {
                      // An empty title (or just the type) becomes the item: "Production - Elevation A".
                      const bare = !f.title.trim() || f.title.trim() === f.jobType || f.title.trim() === `${f.jobType} -`;
                      return {
                        ...f,
                        projectItemId: it?.id ?? "",
                        title: it && bare ? (f.jobType ? `${f.jobType} - ${it.name}` : it.name) : f.title,
                      };
                    })
                  }
                />
              </div>
            ) : null}
            {form.projectId ? (
              <div>
                <Label>Lot</Label>
                <Input placeholder="e.g. Lot 26" list="new-job-lots" value={form.lot} onChange={(e) => setForm({ ...form, lot: e.target.value })} />
                <datalist id="new-job-lots">
                  {projectLots.map((l) => (
                    <option key={l.id} value={l.name}>
                      {[l.address, l.projectItemName].filter(Boolean).join(" · ")}
                    </option>
                  ))}
                </datalist>
              </div>
            ) : null}
            {departments.length > 0 ? (
              <div>
                <Label>Department</Label>
                <Select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
                  <option value="">— route later —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <AddressField value={form.address} onChange={(address) => setForm((f) => ({ ...f, address }))} located={loc} onLocate={setLoc} />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Button type="submit" busy={busy}>
                Create job
              </Button>
              {createError ? <span className="text-xs text-danger">Couldn't create the job: {createError}</span> : null}
            </div>
          </form>
        </Card>
      ) : null}


      <p className="text-[11px] text-steel">
        {visible.length === rows.length
          ? `${rows.length} ${tab === "open" ? "open" : "finished"}`
          : `${visible.length} of ${rows.length} ${tab === "open" ? "open" : "finished"}`}
        {visible.length > page.length ? ` · showing the first ${page.length}` : ""}
      </p>

      <Card className="p-0">
        {grouped
          ? dayGroups.map((g) => (
              <div key={g.label}>
                <div className="border-b border-steel/10 bg-cream/70 px-4 py-1 text-xs font-semibold text-ink/80">
                  {g.label}
                  {g.jobs.some((j) => j.assignedUids.length === 0) ? (
                    <span className="ml-2 font-normal text-brandorange-dark">
                      {g.jobs.filter((j) => j.assignedUids.length === 0).length} unassigned
                    </span>
                  ) : null}
                </div>
                {g.jobs.map((job) => jobRow(job, true))}
              </div>
            ))
          : page.map((job) => jobRow(job, false))}
        {visible.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            {rows.length === 0 ? (tab === "open" ? "No open jobs." : "No finished jobs yet.") : "No jobs match."}
          </div>
        ) : null}
        {visible.length > page.length ? (
          <button
            className="block w-full border-t border-steel/10 px-4 py-2 text-center text-xs text-steel hover:text-ink"
            onClick={() => setLimit((l) => l + PAGE)}
          >
            Show {Math.min(PAGE, visible.length - page.length)} more
          </button>
        ) : null}
      </Card>
    </div>
  );
}

// Admin choice: does finishing a job ask for the customer's signature?
// Off, optional (a "Get sign-off" card on the job), or required before
// "Finish job" works.
const SIGNOFF_LABEL = { off: "never asked", optional: "optional", required: "required before Finish job" } as const;
function SignoffSetting({ tenantId }: { tenantId: string }) {
  const [mode, setMode] = useState<keyof typeof SIGNOFF_LABEL | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenant(tenantId)), (snap) =>
        setMode((snap.get("jobSignoff") as keyof typeof SIGNOFF_LABEL | undefined) ?? "optional"),
      ),
    [tenantId],
  );
  if (!mode) return null;
  return (
    <p className="flex flex-wrap items-center gap-1 text-[11px] text-steel">
      Customer sign-off when a job is finished:
      <Select
        className="w-auto px-2 py-0.5 text-[11px]"
        value={mode}
        disabled={busy}
        onChange={async (e) => {
          setBusy(true);
          setNote("");
          try {
            await call("setJobSignoffMode", { tenantId, mode: e.target.value });
            setNote("Saved.");
          } catch (err) {
            setNote((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {(Object.keys(SIGNOFF_LABEL) as Array<keyof typeof SIGNOFF_LABEL>).map((m) => (
          <option key={m} value={m}>
            {SIGNOFF_LABEL[m]}
          </option>
        ))}
      </Select>
      {note ? <span>{note}</span> : null}
    </p>
  );
}

// Admin opt-in: email the job's payer a completion summary when a job is
// finished. Lives on the Done tab, where finished jobs are.
function CompletionEmailSettings({ tenantId }: { tenantId: string }) {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantNotificationSettings(tenantId)), (snap) =>
        setSettings(snap.exists() ? (snap.data() as NotificationSettings) : null),
      ),
    [tenantId],
  );

  function edit() {
    setEnabled(settings?.completionEmailEnabled ?? false);
    setCc(settings?.completionEmailCc ?? "");
    setNote("");
    setOpen(true);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote("");
    try {
      await call("setNotificationSettings", { tenantId, completionEmailEnabled: enabled, completionEmailCc: cc.trim() });
      setNote("Saved.");
      setOpen(false);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    const on = settings?.completionEmailEnabled === true;
    return (
      <p className="text-[11px] text-steel">
        Completion emails: {on ? "on — the customer gets a summary when a job is finished" : "off"}
        {on && settings?.completionEmailCc ? `, copy to ${settings.completionEmailCc}` : ""} ·{" "}
        <button className="underline hover:text-ink" onClick={edit}>
          edit
        </button>
        {note ? ` · ${note}` : ""}
      </p>
    );
  }
  return (
    <Card>
      <div className="mb-1 text-xs font-semibold text-ink">Completion emails</div>
      <p className="mb-2 text-xs text-steel">
        When a job is marked done, email its bill-to customer (or the job's customer, if there's no separate bill-to)
        the job, PO number, address, completion time and techs. Sent once per job; each send shows on the job's
        timeline. Replies go to your company contact email.
      </p>
      <form onSubmit={save} className="space-y-2">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Email the customer when a job is finished
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Label>Also send a copy to (optional)</Label>
            <Input type="email" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="office@yourcompany.com" />
          </div>
          <Button type="submit" busy={busy}>
            Save
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </form>
      {note ? <p className="mt-1 text-xs text-steel">{note}</p> : null}
    </Card>
  );
}
